<template>
    <div class="AskForVolunteerButton flex w-full flex-col sm:flex-row">
        <div v-if="preview">
            <CButton full size="large" variant="success"> Chcem tohto dobrovoľníka </CButton>
        </div>

        <div v-else>
            <div v-if="profileOwner" class="flex flex-col sm:flex-row">
                <ProfileVisibilityButton
                    v-if="userProfile && visibility"
                    :key="visibility"
                    class="mb-4 mr-0 w-full sm:mb-0 sm:mr-4 sm:w-fit"
                    :profile-id="userId"
                    :slug="userProfile?.slug || ''"
                    type="volunteer"
                    v-model="visibility"
                    @update:model-value="handleVisibilityChange"
                />

                <CButton
                    v-if="profileOwner"
                    class="mb-4 mr-0 w-full sm:mb-0 sm:mr-4 sm:w-fit"
                    full
                    icon="edit"
                    size="large"
                    :to="{
                        name: 'profile-volunteer-preferences',
                        params: { id: volunteerId }
                    }"
                    variant="primary-150"
                >
                    Upraviť profil
                </CButton>

                <ProfilePauseRequestsButton
                    v-if="userProfile && areRequestsActive !== undefined"
                    class="mb-4 mr-0 sm:mb-0 sm:mr-4"
                    :on-submit="submitPauseRequests"
                    :profile-id="userId"
                    :slug="userProfile?.slug || ''"
                    type="volunteer"
                    :value="areRequestsActive"
                />

                <CButton
                    class="ml-0 w-full sm:w-fit"
                    icon="activity"
                    size="large"
                    :to="{
                        name: 'profile-activity',
                        query: {
                            mine: 'true',
                            status: 'waiting,confirm,active'
                        }
                    }"
                    variant="primary-150"
                >
                    Žiadosti
                </CButton>
            </div>

            <CButton v-else :disabled="!canRequest" full size="large" variant="success" @click="showModal">
                <template v-if="canRequest">Chcem tohto dobrovoľníka</template>
                <template v-else>Teraz neprijíma žiadosti</template>
            </CButton>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useModal } from 'vue-final-modal'
import { useExponea } from '@/composables/exponea/useExponea'
import { useCheese } from '@/composables/useCheese'
import { useUser } from '@/composables/user/useUser'

import type { UserProfile, VolunteerProfile } from '#shared/types/User'
import { VisibilityType } from '#shared/types/visibility'
import SignInModal from '@/components/modals/SignInModal.vue'
import AskForVolunteerModal from '@/components/volunteer/AskForVolunteerModal.vue'

// Props
const props = defineProps({
    volunteer: {
        type: Object as () => UserProfile,
        required: true
    },
    volunteerId: {
        type: String,
        required: true
    },
    volunteerFirstName: {
        type: String,
        required: true
    },
    volunteerLastName: {
        type: String,
        required: true
    },
    volunteerSlug: {
        type: String,
        required: true
    },
    preview: {
        type: Boolean,
        default: false
    },
    profileOwner: {
        type: Boolean,
        default: false
    }
})

// Setup
const route = useRoute()
const router = useRouter()
const applicationStore = useApplicationStore()
const { trackExponeaEvent } = useExponea()
const { track: trackCheese } = useCheese()
const { updateUserProfile } = useUser()

// Modal setup
const { open: openAskForVolunteerModal, close: closeAskForVolunteerModal } = useModal({
    component: AskForVolunteerModal,
    attrs: {
        volunteerId: computed(() => props.volunteerId),
        onClose() {
            closeAskForVolunteerModal()
        }
    }
})

const { open: openSignInModal, close: closeSignInModal } = useModal({
    component: SignInModal,
    attrs: {
        onClose() {
            closeSignInModal()
        }
    }
})

// Reactive state
const trackGtmEvent = ref(false)
const trackGtmCode = ref('')
const visibility = ref<VisibilityType>(VisibilityType.draft)

// Computed
const userId = computed(() => applicationStore.userId || '')
const isLoggedIn = computed(() => applicationStore.isLoggedIn)
const userProfile = computed<UserProfile>(() => applicationStore.userProfile as UserProfile)
const volunteerProfile = computed<VolunteerProfile | undefined>(() => userProfile.value?.volunteerProfile || undefined)
const areRequestsActive = computed<boolean>(() => !volunteerProfile.value?.requestsPausedAt)
const canRequest = computed<boolean>(() => !props.volunteer.volunteerProfile?.requestsPausedAt)

// Watch for volunteerProfile changes to update local visibility
watch(() => volunteerProfile.value?.visibility, (newVisibility) => {
    if (newVisibility) {
        visibility.value = newVisibility
    }
}, { immediate: true })

// Methods
function openVolunteerRequestModal() {
    trackCheese('volunteering_request_started', { volunteer_id: props.volunteerId, initiated_by: 'org' })

    trackExponeaEvent({
        eventName: 'cta1_zaujem_o_dobrovolnika',
        userId: props.volunteerId,
        eventProperties: {
            'user_id': props.volunteerId,
            'user_slug': props.volunteerSlug,
            'user_first_name': props.volunteerFirstName,
            'user_last_name': props.volunteerLastName
        }
    })

    openAskForVolunteerModal()
}

function openLogInModal() {
    if (process.client) {
        localStorage.setItem('beforeLogInLocation', route.fullPath)
    }

    openSignInModal()
}

function showModal(): void {
    if (!canRequest.value) return

    if (isLoggedIn.value) {
        openVolunteerRequestModal()
    } else {
        openLogInModal()
    }
}

async function updateUserProfileAction(update: Partial<UserProfile>) {
    await updateUserProfile(userId.value, update)
    await applicationStore.reloadUserProfile()
}

async function submitVisibility(visibility: VisibilityType) {
    try {
        const previousVisibility = volunteerProfile.value?.visibility as VisibilityType

        await updateUserProfile(userId.value, {
            volunteerProfile: {
                ...(volunteerProfile.value as VolunteerProfile),
                visibility
            }
        } as Partial<UserProfile>)

        await applicationStore.reloadUserProfile()

        trackVisibilityChange(previousVisibility, visibility)
        return true
    } catch (error) {
        throw new Error('problem with updating volunteer visibility, error: ' + error)
    }
}

async function submitPauseRequests(active: boolean) {
    try {
        const requestsPausedAt: VolunteerProfile['requestsPausedAt'] = !active ? new Date() : null

        await updateUserProfileAction({
            volunteerProfile: {
                ...(volunteerProfile.value as VolunteerProfile),
                requestsPausedAt
            }
        } as Partial<UserProfile>)

        await applicationStore.reloadUserProfile()

        trackPauseRequestsChange(requestsPausedAt)
        return true
    } catch (error) {
        console.error('submitPauseRequests', error)
        return false
    }
}

async function handleVisibilityChange(newVisibility: VisibilityType) {
    visibility.value = newVisibility
    await submitVisibility(newVisibility)
}

function trackVisibilityChange(previousVisibility: VisibilityType, visibility: VisibilityType) {
    trackExponeaEvent({
        eventName: 'uprava_viditelnosti_profilu_dobrovolnika',
        userId: props.volunteerId,
        eventProperties: {
            'user_id': props.volunteerId,
            'user_slug': props.volunteerSlug,
            'visibility_current': visibility,
            'visibility_previous': previousVisibility
        }
    })

    // Track GTM event with change
    trackGtmCode.value = `event: "user_info", user_id: "${userId.value}", is_volunteer: ${
        visibility === VisibilityType.published
    }`
    trackGtmEvent.value = true
}

function trackPauseRequestsChange(requestsPausedAt: VolunteerProfile['requestsPausedAt']) {
    trackExponeaEvent({
        eventName: 'pozastavenie_ziadosti_dobrovolnika',
        userId: userId.value,
        eventProperties: {
            user_id: userId.value,
            user_slug: userProfile.value.slug,
            requests_paused: Boolean(requestsPausedAt),
            requests_paused_at: requestsPausedAt
        }
    })
}
</script>
